import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { TableHead, Table, TableBody, TableCell, TableContainer, TableRow, TableFooter, TablePagination, Card, Box, Container, Chip, FormLabel, TableSortLabel, Toolbar, Stack } from "@mui/material";
import Api from "../../network/Api";
import TypingIndicator from "../Shared/TypingIndicator";
import { Category, Lesson } from "../../types/Lessons";
import { NavLink } from "react-router-dom";
import { CommentButton, ItemButtonActions, StateButton } from "./Buttons";
import { StartProps } from "../Layouts/MainApp";
import dayjs from "dayjs";
import LessonSearch from "./Search";

const LessonsList = (props: StartProps & ItemButtonActions) => {

  const [lessons, setLessons] = useState<Array<Lesson>>();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [orderBy, setOrderBy] = useState<string>("created_at");
  const [direction, setDirection] = useState<"asc" | "desc">("desc");
  const [categoryFilter, setCategoryFilter] = useState<Array<Category>>([]);
  const scrollTopRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    Api.get(`/lessons?limit=${limit}&page=${page}&order_by=${orderBy}&order=${direction}&category_filter=${categoryFilter.map(cat => cat.id).join(",")}`).then(result => {
      setLessons(result.items);
      setTotal(result.total);
      scrollTopRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, [limit, page, direction, orderBy, categoryFilter]);

  const SortLabel = ({ label, field }: { label: string, field: string }) => (
    <TableSortLabel
      active={orderBy === field}
      direction={direction}
      onClick={() => {
        if (orderBy === field) {
          setDirection(direction === "asc" ? "desc" : "asc");
        } else {
          setOrderBy(field);
        }
      }}
      sx={{color: "inherit !important"}}
    >
      {label}
    </TableSortLabel>
  );

  return <Box sx={{overflowY: 'auto', flexGrow: 1, width: "100%", pt: 3, pb: 5}}>
    <Container maxWidth="xl">
      <Card sx={{p: "0 !important", mb: 2}}>
        <Toolbar ref={scrollTopRef} sx={{gap: 2}}>
          <h3 style={{margin: 0, flexGrow: 1}}>Knowledge base</h3>

          <Stack spacing={1} sx={{flexGrow: 1, my: 1}}>
            <LessonSearch onClick={lesson => navigate(`/lessons/${lesson.id}`)} />

            { categoryFilter.length > 0 && <Stack direction="row" alignItems="center" spacing={1}>
            <FormLabel>Remove filter:</FormLabel>
            { categoryFilter.map(category => 
              <Chip 
                key={category.id}
                label={category.name}
                onDelete={() => setCategoryFilter(prev => prev.filter(current => current.id !== category.id))}
                sx={{m: 0.5}}
              />
            )}
          </Stack> }

          </Stack>
        </Toolbar>
      </Card>
      
      <Card sx={{p: "0 !important"}}>
        <TableContainer sx={{width: '100%', overflowX: 'auto'}}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ position: "sticky", left: 0, zIndex: 1 }}><SortLabel label="Issue" field="headline" /></TableCell>
                <TableCell><SortLabel label="Origin" field="origin" /></TableCell>
                <TableCell align="center"><SortLabel label="Reviewed" field="state" /></TableCell>
                <TableCell align="center">Comments</TableCell>
                <TableCell>Root causes</TableCell>
                <TableCell><SortLabel label="Created at" field="created_at" /></TableCell>
                <TableCell><SortLabel label="Last updated" field="updated_at" /></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lessons ? lessons.map((lesson) => {
                return <TableRow
                  key={lesson.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell sx={{ position: "sticky", left: 0, zIndex: 1, right: 0 }}>
                    <NavLink to={`/lessons/${lesson.id}`}>{lesson.headline}</NavLink>
                  </TableCell>
                  <TableCell>{lesson.origin}</TableCell>
                  <TableCell align="center" width="10%"><StateButton lesson={lesson} onOpen={props.onOpen} /></TableCell>
                  <TableCell align="center" width="15%"><CommentButton lesson={lesson} size="small" onOpen={props.onOpen} /></TableCell>
                  <TableCell>{[...new Set(lesson.causes.map(cause => cause.category))].map(item => <Chip disabled={categoryFilter.length > 0 && categoryFilter.filter(cat => cat.id === item.id).length > 0} key={item.id} label={item.name} sx={{m: 0.5}} onClick={() => setCategoryFilter(prev => [...prev, item])} />)}</TableCell>
                  <TableCell><small className="text-secondary">{dayjs.unix(lesson.created_at).format("lll")}</small></TableCell>
                  <TableCell><small className="text-secondary">{dayjs.unix(lesson.updated_at).format("lll")}</small></TableCell>
                </TableRow>
              }) : <TableRow>
                <TableCell colSpan={5} sx={{justifyItems: "center", padding: 5}}>
                  <TypingIndicator />
                </TableCell>
              </TableRow>}
            </TableBody>

            <TableFooter>
              <TableRow>
                <TablePagination 
                  count={total} 
                  page={page-1}
                  rowsPerPage={limit}
                  onPageChange={(e, page) => setPage(page+1)}
                  onRowsPerPageChange={e => setLimit(e.target.value)}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  </Box>
};

export default LessonsList;
